import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { apiClient } from '@/api/client';
import { useAuth } from '@/context/auth';

interface UsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  organisation_id?: string;
}

export function useSuperAdminUsers(params: UsersQueryParams = {}) {
  const { page = 1, limit = 10, search = '', role, organisation_id } = params;
  const [users, setUsers] = useState<any[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user: currentUser } = useAuth();

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Calculate pagination range
      const startRow = (page - 1) * limit;
      const endRow = page * limit - 1;

      // Build the query
      let query = supabase
        .from('profiles')
        .select('id, name, email, role, organisation_id', { count: 'exact' })
        .order('name')
        .range(startRow, endRow);
      
      // Apply search filter if provided
      if (search) {
        query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%`);
      }
      
      // Apply role filter if provided
      if (role) {
        query = query.eq('role', role);
      }
      
      // Apply organisation filter if provided
      if (organisation_id === 'none') {
        query = query.is('organisation_id', null);
      } else if (organisation_id) {
        query = query.eq('organisation_id', organisation_id);
      }
      
      // Execute the query
      const { data: userData, error: queryError, count } = await query;
      
      if (queryError) {
        throw new Error(`Error fetching users: ${queryError.message}`);
      }
      
      if (userData) {
        // For each user, get their organisation name if they have one
        const usersWithOrgNames = await Promise.all(
          userData.map(async (user) => {
            if (user.organisation_id) {
              const { data: orgData, error: orgError } = await supabase
                .from('organisations')
                .select('name')
                .eq('id', user.organisation_id)
                .single();
              
              return {
                ...user,
                organisation_name: orgData?.name || 'Unknown Organisation'
              };
            }
            return user;
          })
        );
        
        setUsers(usersWithOrgNames);
        setTotalUsers(count || 0);
      } else {
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching users');
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, search, role, organisation_id]);

  const fetchUserDetail = useCallback(async (userId: string) => {
    try {
      // Get user data
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (userError) {
        throw new Error(`Error fetching user details: ${userError.message}`);
      }
      
      // Get user's restaurant access
      const { data: restAccess, error: restAccessError } = await supabase
        .from('user_restaurant_access')
        .select(`
          id, 
          restaurant_id, 
          access_level,
          restaurants:restaurant_id (restaurant_name)
        `)
        .eq('user_id', userId);
      
      if (restAccessError) {
        try {
          console.error('Error fetching restaurant access:', restAccessError);
        } catch (logError) {
          console.error('Failed to log restAccessError:', logError);
        }
      }
      
      // Get user's organisation access
      const { data: orgAccess, error: orgAccessError } = await supabase
        .from('user_organisation_access')
        .select(`
          id,
          organisation_id,
          access_level,
          organisations:organisation_id (name)
        `)
        .eq('user_id', userId);
      
      if (orgAccessError) {
        try {
          console.error('Error fetching organisation access:', orgAccessError);
        } catch (logError) {
          console.error('Failed to log orgAccessError:', logError);
        }
      }
      
      // Format the restaurant access data
      const formattedRestAccess = (restAccess || []).map(access => ({
        ...access,
        restaurant_name: access.restaurants?.restaurant_name || 'Unknown Restaurant'
      }));
      
      // Format the organisation access data
      const formattedOrgAccess = (orgAccess || []).map(access => ({
        ...access,
        organisation_name: access.organisations?.name || 'Unknown Organisation'
      }));
      
      return {
        success: true,
        user: userData,
        access: {
          restaurants: formattedRestAccess,
          organisations: formattedOrgAccess
        }
      };
    } catch (err: any) {
      throw err;
    }
  }, []);

  const createUser = useCallback(async (userData: any) => {
    try {
      const result = await apiClient.post('/api/admin/users', userData);

      if (result.data.success) {
        return result.data.user;
      } else {
        throw new Error(result.data.error || 'Failed to create user');
      }
    } catch (err: any) {
      throw err;
    }
  }, []);

  const updateUser = useCallback(async (userId: string, userData: any) => {
    try {
      const result = await apiClient.put(`/api/admin/users/${userId}`, userData);

      if (result.data.success) {
        return result.data.user;
      } else {
        throw new Error(result.data.error || 'Failed to update user');
      }
    } catch (err: any) {
      throw err;
    }
  }, []);

  const updateUserRole = useCallback(async (userId: string, role: string) => {
    try {
      const result = await apiClient.put(`/api/admin/users/${userId}/role`, { role });

      if (result.data.success) {
        // If this updates the current user's role, JWT claims will need to be refreshed
        if (userId === currentUser?.id && result.data.requires_refresh) {
          // Log out the user after a delay to give them time to see the success message
          setTimeout(() => {
            supabase.auth.signOut();
          }, 5000);
        }
        
        return result.data;
      } else {
        throw new Error(result.data.error || 'Failed to update user role');
      }
    } catch (err: any) {
      throw err;
    }
  }, [currentUser]);

  const sendPasswordReset = useCallback(async (userId: string) => {
    try {
      const result = await apiClient.post(`/api/admin/users/${userId}/reset-password`);

      if (result.data.success) {
        return result.data;
      } else {
        throw new Error(result.data.error || 'Failed to send password reset');
      }
    } catch (err: any) {
      throw err;
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    totalUsers,
    isLoading,
    error,
    refetch: fetchUsers,
    fetchUserDetail,
    createUser,
    updateUser,
    updateUserRole,
    sendPasswordReset,
  };
}